import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Typography, Space, Card, Radio, Tag, Checkbox, Divider, Alert, Segmented } from 'antd';
import {
  ReloadOutlined,
  PieChartOutlined,
  ColumnHeightOutlined,
  SplitCellsOutlined,
  MergeCellsOutlined,
  BulbOutlined,
  UserOutlined,
  CheckCircleOutlined,
  AimOutlined,
  PercentageOutlined,
  DashboardOutlined
} from '@ant-design/icons';
import { Mafs, Coordinates, Line, Point } from 'mafs';

const { Title, Paragraph, Text } = Typography;

/**
 * FractionModel Flagship Component
 * - Permanent Target matching (success = Correct Fraction)
 * - Comparative Toggle for Remediation
 * - Semantic Icons in Status Metrics
 */
export const FractionModel = ({ config, content }) => {
  const safe = (n, fallback) => (Number.isFinite(n) ? n : fallback);
  const clampInt = (n, lo, hi) => Math.min(hi, Math.max(lo, Math.round(n)));

  const gcd = (a, b) => {
    let x = Math.abs(Math.trunc(a)), y = Math.abs(Math.trunc(b));
    while (y !== 0) { const t = x % y; x = y; y = t; }
    return x || 1;
  };

  const cfg = config || {};
  const explanation = content?.explanation || "Explore equivalent fractions through interactive shading.";
  const isInteractive = cfg.interactive !== false;

  const hasStudentAnswer = !!cfg.studentAnswerFraction;
  const [viewMode, setViewMode] = useState('CORRECT');

  // --- Fraction Definitions ---
  // Correct Answer (Source of Truth for Success)
  const correctNum = safe(cfg.fraction?.numerator, 1);
  const correctDen = clampInt(safe(cfg.fraction?.denominator, 4), 1, 48);

  // Student Answer (Misconception Mapping)
  const studentNum = safe(cfg.studentAnswerFraction?.numerator, correctNum);
  const studentDen = clampInt(safe(cfg.studentAnswerFraction?.denominator, correctDen), 1, 48);

  // Active View State
  const activeNum = viewMode === 'STUDENT' ? studentNum : correctNum;
  const activeDen = viewMode === 'STUDENT' ? studentDen : correctDen;

  const [model, setModel] = useState(cfg.model === 'BAR' ? 'BAR' : 'CIRCLE');
  const [showNumberLine, setShowNumberLine] = useState(cfg.showNumberLine === true);
  const [cuts, setCuts] = useState(1);

  const partsPerUnit = activeDen * cuts;
  const absTargetParts = Math.abs(activeNum) * cuts;
  const targetSign = activeNum < 0 ? -1 : 1;
  const unitsShown = clampInt(Math.ceil(absTargetParts / partsPerUnit), 1, 6);

  const makeMatrix = (units, perUnit, fillParts) => {
    const out = Array.from({ length: units }, () => Array.from({ length: perUnit }, () => false));
    let remaining = fillParts;
    for (let u = 0; u < units; u++) {
      for (let i = 0; i < perUnit; i++) {
        if (remaining <= 0) return out;
        out[u][i] = true; remaining--;
      }
    }
    return out;
  };

  const [shaded, setShaded] = useState(() => makeMatrix(unitsShown, partsPerUnit, Math.min(absTargetParts, unitsShown * partsPerUnit)));
  const dragRef = useRef({ dragging: false, mode: true });

  useEffect(() => {
    setShaded(makeMatrix(unitsShown, partsPerUnit, Math.min(absTargetParts, unitsShown * partsPerUnit)));
  }, [unitsShown, partsPerUnit, absTargetParts, viewMode]);

  // --- Success Metrics ---
  const currentParts = useMemo(() => shaded.reduce((acc, row) => acc + row.filter(Boolean).length, 0), [shaded]);

  // Success is strictly defined as reaching the correct target
  const match = useMemo(() => {
    const userVal = currentParts / partsPerUnit;
    const correctVal = Math.abs(correctNum / correctDen);
    const diff = Math.abs(userVal - correctVal);
    return diff < 1e-9 ? 100 : Math.max(0, Math.round((1 - diff) * 100));
  }, [currentParts, partsPerUnit, correctNum, correctDen]);

  const simplify = (n, d) => { const g = gcd(n, d); return { n: n / g, d: d / g }; };
  const currentFrac = simplify(targetSign * currentParts, partsPerUnit);
  const targetFracDisplay = simplify(activeNum, activeDen);
  const correctFracDisplay = simplify(correctNum, correctDen);

  const toggleCell = (u, i) => {
    if (!isInteractive) return;
    setShaded(prev => {
      const next = prev.map(row => [...row]);
      next[u][i] = !next[u][i];
      dragRef.current.mode = next[u][i];
      return next;
    });
  };

  const renderBarModel = () => (
      <div style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}>
        {shaded.map((row, u) => (
            <div key={u} className="ch-fraction-bar-unit" style={{ gridTemplateColumns: `repeat(${partsPerUnit}, 1fr)`, marginBottom: '10px' }}>
              {row.map((isOn, i) => (
                  <button
                      key={i}
                      className={`ch-fraction-cell ${isOn ? 'ch-fraction-cell-shaded' : ''}`}
                      style={{
                        backgroundColor: isOn ? '#1890ff' : '#ffffff',
                        pointerEvents: isInteractive ? 'auto' : 'none',
                        height: '40px',
                        border: '1px solid #475569'
                      }}
                      onPointerDown={(e) => { e.preventDefault(); dragRef.current.dragging = true; toggleCell(u, i); }}
                      onPointerEnter={() => { if (dragRef.current.dragging) {
                        setShaded(prev => { const next = prev.map(r => [...r]); next[u][i] = dragRef.current.mode; return next; });
                      }}}
                  />
              ))}
            </div>
        ))}
      </div>
  );

  const renderCircleModel = () => {
    const H = 240; const r = 70; const spacing = 180; const cy = H / 2;
    const svgW = Math.max(700, unitsShown * spacing);
    return (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <svg width={svgW} height={H} viewBox={`0 0 ${svgW} ${H}`} style={{ background: "#fff", display: 'block', margin: '0 auto' }}>
            {shaded.map((row, u) => {
              const cx = 100 + u * spacing;
              return (
                  <g key={u}>
                    <circle cx={cx} cy={cy} r={r} fill="#ffffff" stroke="#475569" strokeWidth="2" />
                    {row.map((isOn, i) => {
                      const angleStep = (Math.PI * 2) / partsPerUnit;
                      const start = -Math.PI / 2 + i * angleStep;
                      const end = start + angleStep;
                      const d = `M ${cx} ${cy} L ${cx + r * Math.cos(start)} ${cy + r * Math.sin(start)} A ${r} ${r} 0 ${angleStep > Math.PI ? 1 : 0} 1 ${cx + r * Math.cos(end)} ${cy + r * Math.sin(end)} Z`;
                      return <path key={i} d={d} fill={isOn ? "#1890ff" : "#ffffff"} stroke="#475569" strokeWidth="1" onClick={() => toggleCell(u, i)} />;
                    })}
                  </g>
              );
            })}
          </svg>
        </div>
    );
  };

  const renderNumberLine = () => {
    const totalValue = currentParts / partsPerUnit;
    const correctValue = Math.abs(correctNum / correctDen);
    const studentValue = Math.abs(studentNum / studentDen);
    const xHi = Math.max(2, totalValue, correctValue, studentValue) + 0.5;

    return (
        <Mafs viewBox={{ x: [-0.2, xHi], y: [-0.5, 0.5] }} height={110} pan={false} zoom={false}>
          <Coordinates.Cartesian xAxis={{ lines: true, labels: (x) => String(Number(x.toFixed(2))) }} yAxis={{ lines: false }} />
          <Line.Segment point1={[0, 0]} point2={[totalValue, 0]} color="#1890ff" weight={6} />
          <Point x={correctValue} y={0} color="#52c41a" />
          {hasStudentAnswer && <Point x={studentValue} y={0} color="#fa8c16" opacity={0.6} />}
          <Point x={totalValue} y={0} color="#1890ff" />
        </Mafs>
    );
  };

  return (
      <div className="ch-fraction-model animate-slide-up" onPointerUp={() => dragRef.current.dragging = false}>
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Card size="small" className="ch-shapeshifter-header">
            <div className="flexSpaceBetween flexAlignCentre">
              <Space>
                <Title level={4} style={{ margin: 0 }}>Fraction Model</Title>
                {hasStudentAnswer && (
                    <Segmented value={viewMode} onChange={setViewMode}
                               options={[
                                 { label: 'Correct', value: 'CORRECT', icon: <CheckCircleOutlined /> },
                                 { label: 'Yours', value: 'STUDENT', icon: <UserOutlined /> },
                               ]}
                    />
                )}
                <Tag color="blue">Target: {correctFracDisplay.n}/{correctFracDisplay.d}</Tag>
                <Tag color={match === 100 ? "success" : "orange"}>Match: {match}%</Tag>
              </Space>
              <Button icon={<ReloadOutlined />} size="small" onClick={() => setCuts(1)}>Reset</Button>
            </div>
          </Card>

          <Paragraph type="secondary" italic>{explanation}</Paragraph>

          <div className="ch-emph-block" style={{ padding: '16px', borderRadius: '12px', background: '#f0f2f5' }}>
            <div className="flexSpaceBetween">
              <Space size="large">
                <Radio.Group value={model} onChange={e => setModel(e.target.value)} buttonStyle="solid">
                  <Radio.Button value="CIRCLE"><PieChartOutlined /> Circle</Radio.Button>
                  <Radio.Button value="BAR"><ColumnHeightOutlined /> Bar</Radio.Button>
                </Radio.Group>
                <Checkbox checked={showNumberLine} onChange={e => setShowNumberLine(e.target.checked)}>Number Line</Checkbox>
              </Space>
              <Space>
                <Text strong>Equivalence:</Text>
                <Button icon={<SplitCellsOutlined />} onClick={() => setCuts(c => Math.min(c * 2, 12))}>Split x2</Button>
                <Button icon={<MergeCellsOutlined />} disabled={cuts % 2 !== 0} onClick={() => setCuts(c => Math.max(1, c / 2))}>Combine ÷2</Button>
              </Space>
            </div>
          </div>

          <div className="ch-fraction-stage" style={{ padding: '20px', border: '1px solid #d9d9d9', borderRadius: '12px', background: '#fff' }}>
            {model === 'BAR' ? renderBarModel() : renderCircleModel()}
            {showNumberLine && (
                <>
                  <Divider style={{ margin: '24px 0 12px 0' }} />
                  <Text strong style={{ color: '#475569' }}>Number Line Representation</Text>
                  {renderNumberLine()}
                </>
            )}
          </div>

          <div className="ch-fraction-metrics">
            <div className="ch-fraction-metric-card">
              <div className="flexAlignCentre" style={{ marginBottom: 4 }}>
                <PercentageOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                <Text type="secondary">Current Shaded</Text>
              </div>
              <div className="ch-fraction-math">{currentFrac.n}/{currentFrac.d}</div>
            </div>

            <div className="ch-fraction-metric-card" style={{ borderLeft: '3px solid #1890ff' }}>
              <div className="flexAlignCentre" style={{ marginBottom: 4 }}>
                {viewMode === 'STUDENT' ? <UserOutlined style={{ marginRight: 8, color: '#fa8c16' }} /> : <AimOutlined style={{ marginRight: 8, color: '#52c41a' }} />}
                <Text type="secondary">{viewMode === 'STUDENT' ? 'Viewing Yours' : 'Viewing Correct'}</Text>
              </div>
              <div className="ch-fraction-math" style={{ color: viewMode === 'STUDENT' ? '#fa8c16' : '#1890ff' }}>{targetFracDisplay.n}/{targetFracDisplay.d}</div>
            </div>

            <div className="ch-fraction-metric-card">
              <div className="flexAlignCentre" style={{ marginBottom: 4 }}>
                <DashboardOutlined style={{ marginRight: 8, color: match === 100 ? '#52c41a' : '#faad14' }} />
                <Text type="secondary">Match with Goal</Text>
              </div>
              <div className={`ch-fraction-math ${match === 100 ? 'text-success' : ''}`}>
                {match === 100 ? 'Matched!' : `${match}%`}
              </div>
            </div>
          </div>
        </Space>
      </div>
  );
};